import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { message, Spin } from "antd";
import { DownOutlined } from "@ant-design/icons";
import "./LoginNewPage.scss";
import _BankAdminService from "../../modules/BankAdminService";
import { processMicrosoftRedirectResult, startMicrosoftLogin } from "../../modules/msalService";
import { SESSION_TOKEN } from "../../constants/constants";
import { routes } from "../../routes/Routes";
import { LOAN_OFFICER_ROLE_ID } from "../../constants/DataConstants";

const roles = [
  {
    title: "Client",
    description:
    "Login and Access your applications, documents, and application details.",
    path: "/login/client",
  },
  {
    title: "Institution",
    description:
    "Access for institutional users, including Super Admin, Bankers, Loan Officer, Compliance Admin and Wealth Advisor.",
    path: "/login/banker",
  },
  // {
  //   title: "Super Admin",
  //   description:
  //     "Manage user roles and configure recommendation and approval limits for each role.",
  //   path: "/login/super-admin",
  // },
  // {
  //   title: "Loan Officer",
  //   description:
  //   "Review loan applications and make final approval or rejection decisions.",
  //   path: "/login/loan-officer",
  // },
  // {
  //   title: "Compliance Admin",
  //   description:
  //   "Initiate the AML/KYC process, enter the correct CBR , upload the CBR report, and complete the compliance review.",
  //   path: "/login/compliance-admin",
  // },
];

export default function LoginNewPage() {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const history = useHistory();
  // True only while the post-Microsoft-redirect ValidateMicrosoftToken round trip is in
  // flight, so the brief landing-page flash before the dashboard redirect shows a loader
  // instead of the bare hero page.
  const [ssoProcessing, setSsoProcessing] = useState(false);

  // Institution card click: if tenant uses SSO (non-SQL), go straight to Microsoft
  // login without showing the institutional portal form. SQL tenants navigate normally.
  const handleInstitutionClick = async (e) => {
    e.preventDefault();
    try {
      const stored = localStorage.getItem("tenantInfo");
      const tenantInfo = stored ? JSON.parse(stored) : null;
      const ssoProvider = (tenantInfo?.SSOProvider || "").trim().toLowerCase();
      if (ssoProvider && ssoProvider !== "sql") {
        await startMicrosoftLogin();
        // Browser navigates away to Microsoft — nothing runs after this.
      } else {
        history.push("/login/banker");
      }
    } catch (err) {
      console.error("[SSO] Institution click error:", err);
      history.push("/login/banker");
    }
  };

  // After Microsoft SSO redirect, the browser returns to "/" (this page) with
  // #code=... in the URL. Detect and process it, then navigate to the dashboard.
  useEffect(() => {
    async function handleSsoRedirect() {
      const fragment = window.location.hash.substring(1);
      const params = new URLSearchParams(fragment || window.location.search.substring(1));
      if (!params.get("code")) return;

      setSsoProcessing(true);
      try {
        const ssoData = await processMicrosoftRedirectResult();
        if (!ssoData) return;

        const result = ssoData?.backendResult?.Obj;
        const isSuccess = ssoData?.backendResult?.IsSuccess;

        if (!isSuccess || !result?.UserFoundInDatabase) {
          console.error("[SSO] User not found or backend rejected:", ssoData?.backendResult?.Message);
          message.error(
            ssoData?.backendResult?.Message ||
            "Your Microsoft account is not registered in this system. Please contact your administrator."
          );
          return;
        }
        if (!result?.AuthToken) {
          console.error("[SSO] No auth token received.");
          message.error("Authentication failed: no session token received. Please try again.");
          return;
        }

        localStorage.setItem(SESSION_TOKEN, result.AuthToken);
        localStorage.setItem("userRole", result.Role || "");
        localStorage.setItem("RoleId", result.RoleId || "");

        switch (result.UserType) {
          case "Banker": {
            // LoanOfficer table is going away -- Loan Officer is now a Banker+BankerRoles
            // member too, so UserType is always "Banker" here; RoleId is what tells us the
            // active role is actually Loan Officer and should land on that dashboard instead.
            const isLoanOfficer = result.RoleId === LOAN_OFFICER_ROLE_ID;

            localStorage.setItem("IsLoanofficerFlag", JSON.stringify(isLoanOfficer));
            localStorage.setItem("BankerEmailId", result.Email || "");
            localStorage.setItem("BankerId", result.AppUserId || "");
            localStorage.setItem("BankerFirstName", result.DisplayName || "");
            if (result.BankAdminId) localStorage.setItem("BankAdminId", result.BankAdminId);
            if (result.listUserRoles) localStorage.setItem("listUserRoles", JSON.stringify(result.listUserRoles));

            if (isLoanOfficer) {
              localStorage.setItem("LoanOfficerEmailId", result.Email || "");
              localStorage.setItem("LoanOfficerId", result.AppUserId || "");
              localStorage.setItem("LoanOfficerFirstName", result.DisplayName || "");
              history.push(routes.loanOfficerDashboard.path);
            } else {
              history.push(routes.bankerdashboard.path);
            }
            break;
          }
          case "BankAdmin":
            localStorage.setItem("IsLoanofficerFlag", JSON.stringify(false));
            localStorage.setItem("IsBankAdminFlag", JSON.stringify(true));
            localStorage.setItem("BankAdminEmailId", result.Email || "");
            localStorage.setItem("BankAdminId", result.AppUserId || "");
            localStorage.setItem("BankAdminFirstName", result.DisplayName || "");
            history.push(routes.bankadmindashboard.path);
            break;
          case "LoanOfficer":
            localStorage.setItem("IsLoanofficerFlag", JSON.stringify(true));
            localStorage.setItem("LoanOfficerEmailId", result.Email || "");
            localStorage.setItem("LoanOfficerId", result.AppUserId || "");
            localStorage.setItem("LoanOfficerFirstName", result.DisplayName || "");
            if (result.listUserRoles) localStorage.setItem("listUserRoles", JSON.stringify(result.listUserRoles));
            history.push(routes.loanOfficerDashboard.path);
            break;
          case "ComplianceAdmin":
            history.push(routes.complianceadmindashboard.path);
            break;
          default:
            history.push(routes.dashboard.path);
            break;
        }
      } catch (err) {
        console.error("[SSO] Redirect processing failed:", err);
      } finally {
        setSsoProcessing(false);
      }
    }
    handleSsoRedirect();
  }, []);

  // Fetch tenant SSO config once on page load and cache it so every
  // role-login page can read it from localStorage without an extra API call.
  // Also derive isSSOLogin: true when SSOProvider is not "SQL" (e.g. "Microsoft"),
  // so the Switch View role-switch API calls know to skip password validation.
  useEffect(() => {
    async function fetchTenantInfo() {
      try {
        const res = await _BankAdminService.getTenantInformation();
        if (res?.data?.IsSuccess && res.data.Obj) {
          localStorage.setItem("tenantInfo", JSON.stringify(res.data.Obj));
          const ssoProvider = res.data.Obj.SSOProvider || "";
          const isSSOLogin = ssoProvider.trim().toLowerCase() !== "sql";
          localStorage.setItem("isSSOLogin", JSON.stringify(isSSOLogin));
        }
      } catch (err) {
        // Non-fatal – SSO button simply won't appear if this fails
        console.warn("Could not load tenant SSO config:", err);
      }
    }
    fetchTenantInfo();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
    {ssoProcessing && (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0, 0, 0, 0.65)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
        }}
      >
        <Spin size="large" />
        <div style={{ color: "#fff", marginTop: 16, fontSize: 16 }}>Signing you in...</div>
      </div>
    )}
    <div className="lnp-root">
    <div className="login-page-header">
      <div className="login-page-logo">
        <img src="/websiteLogo.png" alt="Levridge Logo" />
      </div>
      <div className="login-wrapper" ref={dropdownRef}>
        <button
          className="login-btn"
          onClick={() => setOpen(!open)}
        >
          Log In
          <DownOutlined className={`arrow ${open ? "open" : ""}`} />
        </button>

        {open && (
          <div className="login-dropdown">
            {roles.map((role, index) => (
              <a
                key={index}
                href={role.path}
                onClick={role.title === "Institution" ? handleInstitutionClick : undefined}
                className="role-card"
              >
                <h4>{role.title}</h4>
                <p>{role.description}</p>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>

    <div className="lnp-hero" style={{ backgroundImage: "url('/levridge2.png')" }}>
      {/* <div className="lnp-hero-overlay">
        <h1 className="lnp-hero-title">Levridge Capital</h1>
        <p className="lnp-hero-subtitle">Levridge Capital is the platform of Levridge Capital, Inc</p>
      </div> */}
    </div>
    </div>
    </>
  );
}